<template>
  <div v-if="props?.campaign" class="campaign-management-wrapper">
    <div class="campaign-header">
      <div class="texts">
        <span class="title">{{ $t('app.campaign.management.header') }}</span>
      </div>
    </div>
    <div class="separator"></div>
    <transition name="fade" mode="out-in">
      <div class="campaign-body">
        <div class="section">
          <div class="section-title" data-step="campaign-infos">
            <p>{{ $t('app.campaign.management.informations.title') }}</p>
            <Button icon="bi-pencil" class="btn icon icon-left outline-white sm" :label="$t('other.actions.edit')" @click="openPanel(PanelType.CHANGE_CAMPAIGN_NAME)"></Button>
          </div>
          <div class="campaign-informations">
            <div class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.name') }}</h4>
              <span>{{ campaign?.campaign?.internal_name }}</span>
            </div>
            <div v-if="!!campaign?.campaign?.locale" class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.language') }}</h4>
              <span>{{ $t(`other.languages.${campaign.campaign.locale === 'fr-FR' ? 'fr' : 'en'}`) }}</span>
            </div>
            <div v-if="!!campaign?.assessment?.environment" class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.env') }}</h4>
              <span>{{ $t(`app.campaign.newPage.slidingPanel.environment.${campaign.assessment.environment}.title`) }}</span>
            </div>
            <div v-if="campaign?.documents?.report" class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.report') }}</h4>
              <span>{{ reportType }}</span>
            </div>
            <div class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.link') }}</h4>
              <div class="inline">
                <span class="tag outline fill" :class="{ 'green': !!publicInvitation?.id, 'grey-light': !publicInvitation?.id }" >{{ publicLinkInfo }}</span>
                <i v-if="publicInvitation?.id" class="bi bi-link-45deg link-icon" @click="copyLink" v-tooltip="$t('app.campaign.header.copyButton.tooltip')"></i>
              </div>
            </div>
            <div class="campaign-informations__item">
              <h4>{{ $t('app.campaign.management.informations.startedAt') }}</h4>
              <span>{{ getDate(campaign.created_at, "LL") }}</span>
            </div>
          </div>
        </div>
        <div class="separator"></div>
        <div class="weighting">
          <div class="section-title" @click="state.isAssessedSkillsSectionOpen = !state.isAssessedSkillsSectionOpen" data-step="campaign-weighting">
            <div class="inline">
              <p style="user-select: none;">{{ $t('app.campaign.newPage.recap.evaluationSection.title') }}</p>
              <i v-if="state.isAssessedSkillsSectionOpen" class="bi bi-caret-down-fill" ></i>
              <i v-else class="bi bi-caret-right-fill" ></i>
            </div>
            <Button icon="bi-pencil" class="btn icon icon-left outline-white sm" :label="$t('other.actions.edit')" @click="openPanel(PanelType.CHANGE_WEIGHTING)"></Button>
          </div>
          <transition name="fade" mode="out-in">
            <WeightingsRecap
              v-if="state.isAssessedSkillsSectionOpen"
              :requiredSkills="requiredSkillsLabels"
              :importantSkills="importantSkillsLabels"
              :secondarySkills="secondarySkillsLabels"
              :ignoredSkills="ignoredSkillsLabels"
              :allModules="campaign?.assessment?.modules"
              :campaignVersion="campaign?.campaign_version"
            >
            </WeightingsRecap>
          </transition>
        </div>
        <div class="separator"></div>
        <div class="section-title" data-step="campaign-card">
          <p>{{ $t('app.campaign.management.customize.title') }}</p>
          <Button icon="bi-pencil" class="btn icon icon-left outline-white sm" :label="$t('other.actions.edit')" @click="openPanel(PanelType.CAMPAIGN_OPTIONS)"></Button>
        </div>
        <div class="separator"></div>
        <div class="consultant-table" data-step="campaign-consultants">
          <div class="table-header">
            <div class="information">
              <!-- <i class="bi bi-person-gear"></i> -->
              <span>{{ $t('app.campaign.management.consultantTable.title') }}</span>
            </div>
            <Button class="icon icon-left sm outline-white put-to-end" icon="bi-plus" :label="$t('app.campaign.management.consultantTable.addConsultant')" @click="openPanel(PanelType.ADD_CONSULTANT)"></Button>
          </div>
          <ConsultantsTable :campaign-id="campaign.id"></ConsultantsTable>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import Button from '@/components/global/Button.vue';

import { useMembersDetails } from '@/queries/members.query';
import { useAllOrgnisationRoles } from '@/queries/organisation.query';

import { cloneDeep } from "lodash";
import { getDate } from '@/composables/utils';
import { MemberRole } from '@/types/role';
import { Campaign } from '@/types/campaign';
import WeightingsRecap from '@/components/campaigns/weighting/WeightingsRecap.vue';
import { useCampaignPublicInvitation } from '@/queries/invitation.query';
import { useToast } from 'vue-toastification';
import ConsultantsTable from '@/components/global/tables/ConsultantsTable.vue';

enum PanelType {
  CHANGE_CAMPAIGN_NAME = 'edit-informations',
  CHANGE_WEIGHTING = 'edit-weightings',
  CAMPAIGN_OPTIONS = 'edit-card',
  ADD_CONSULTANT = 'add-consultant',
}

const { skills } = useSkillsStore();
const { t } = useI18n();
const toast = useToast();

const { data: userDetails, isPending: isUserDetailsPending } = useMembersDetails();
const { data: allRoles, isPending: isRolesPending } = useAllOrgnisationRoles();

const state = reactive<{
  isAssessedSkillsSectionOpen: boolean,
}>({
  isAssessedSkillsSectionOpen: true,
});

const emit = defineEmits(['close', 'openSubPanel']);

const props = defineProps<{
  campaign: Campaign,
}>();


const { data: publicInvitation, isPending: isPublicInvitationPending } =  useCampaignPublicInvitation(props?.campaign?.id);

const campaignShareLink = computed(() => {
  if (!props.campaign || isPublicInvitationPending.value || !publicInvitation.value?.id)
    return null;

  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    publicInvitation.value.id
  }`;
});

async function copyLink() {
  try {
    await navigator.clipboard.writeText(campaignShareLink.value);
    toast.success(t('app.campaign.header.copyButton.successToast'));
  } catch (err) {
    toast.error(t('app.campaign.header.copyButton.errorToast'));
    console.error('Failed to copy:', err);
  }
}

const tableFields = computed(() => {
  return [
    {
      id: "email",
      label: t('app.table.email'),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "last_name",
      label: t('app.table.lastName'),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "first_name",
      label: t('app.table.firstName'),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ];
});

const reportType = computed(() => {
  if (props.campaign.documents.report.send_to_talent && props.campaign.documents.report.format === 'simplified') {
    return t('app.campaign.newPage.slidingPanel.options.report.simplified');
  } else if (props.campaign.documents.report.send_to_talent && props.campaign.documents.report.format === 'complete') {
    return t('app.campaign.newPage.slidingPanel.options.report.complete');
  } else {
    return t('app.campaign.newPage.slidingPanel.options.report.none');
  }
});

const publicLinkInfo = computed(() => {
  if (!!publicInvitation?.value?.id && publicInvitation.value.requires_approval) {
    return t('app.campaign.management.link.requiresApproval');
  } else if (!!publicInvitation?.value?.id && !publicInvitation.value.requires_approval) {
    return t('app.campaign.management.link.activatedAutomatically');
  } else {
    return t('app.campaign.management.link.disabled');
  }
});

const campaignConsultants = computed(() => {
  return userDetails.value.filter((admin) => {
    const adminRole = allRoles.value.find((role) => admin.roles_ids.includes(role.id));
    if (adminRole.role_backward_compatibility_id <= MemberRole.CONSULTANT) return true;
    return false;
  })
});

const requiredSkillsLabels = computed(() => {
  if (typeof props?.campaign.assessment.weighting === 'string') return [];
  return props?.campaign.assessment.weighting?.required || [];
});

const importantSkillsLabels = computed(() => {
  if (typeof props?.campaign.assessment.weighting === 'string') return [];
  const important = cloneDeep(props?.campaign.assessment.weighting?.primary || []);
  return important;
});

const secondarySkillsLabels = computed(() => {
  if (typeof props.campaign.assessment.weighting === 'string') return [];
  return props?.campaign.assessment.weighting?.secondary || [];
});

const ignoredSkillsLabels = computed(() => {
  if (typeof props?.campaign.assessment.weighting === 'string') return [];
  const ignored = cloneDeep(props?.campaign.assessment.weighting?.ignored || []);
  return ignored;
});

function openPanel(panel: PanelType) {
  emit('openSubPanel', panel)
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.small-text {
  font-size: 13px;
}

.inline {
  display: flex;
  gap: 4px;
  align-items: center;
}

.put-to-end {
  margin-left: auto;
}

.separator {
  width: 100%;
  height: 1px;
  background-color: var(--light-grey-color);
}

.link-icon {
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  background-color: var(--white-color);
  border: 1px solid var(--light-grey-color);

  &:hover {
    background-color: var(--light-grey-color);
  }
}

.campaign-management-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 900px;
  padding: 1.5em;

  .campaign-header {
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-bottom: 24px;

    .logo-wrapper {
      position: relative;
      width: 90px;
      height: 90px;

      .logo {
        width: 90px;
        height: 90px;
      }

      .edit-logo {
        position: absolute;
        top: -12px;
        left: -12px;
      }
    }

    .texts {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .title {
        font-size: 20px;
        font-weight: 700;
      }

      .description {
        font-size: 12px;
        color: $secondary-100-color;
      }

      .started-at {
        font-size: 10px;
        color: $secondary-100-color;
      }
    }
  }

  .campaign-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-top: 24px;
    gap: 24px;

    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      p {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }
    }

    .campaign-informations {
      display: grid;
      grid-template-columns: auto auto;
      row-gap: 12px;

      padding: 16px;
      margin-top: 16px;

      border: 1px solid var(--light-grey-color);
      background-color: var(--light-white-color);
      border-radius: 6px;

      .campaign-informations__item {
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;
        //border-bottom: 1px solid var(--light-grey-color);

        h4 {
          font-size: 12px;
          font-weight: 600;
          color: $secondary-100-color;
          margin-bottom: 0;
        }

        span {
          font-weight: 500;
          font-size: 14px;
        }
      }
    }

    .weighting {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .skill-card-list {
        margin: 8px 0;
        display: flex;
        gap: 10px;
        align-items: center;
  
        .skills-card {
          flex: 1;
          align-self: stretch;
          min-height: 150px;
        }
      }
    }

    .table-header {
      display: flex;
      gap: 8px;
      align-items: center;
      padding-bottom: 12px;

      .information {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0px;
        font-weight: 600;

        border-radius: 4px;

        span {
          font-size: 16px;
        }

        i {
          font-size: 20px;
        }
      }
    }

    .consultant-table {
      display: flex;
      flex-direction: column;
      height: 450px;
      margin-top: 12px;
      margin-bottom: 12px;

      ::v-deep(.table-wrapper) {
        max-height: 400px;
      }

      ::v-deep(table) {
        background-color: var(--white-color);
      }
    }
  }
}
</style>